import React from 'react';
import { Autocomplete, Chip, Grid, TextField, Typography } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { SnackContext } from 'src/store/ContextStore';
import { APICONSTANTS } from 'src/components/Constants';
import axios from 'src/axios';

export default function ExternalFamilyIds({
  user,
  onDirtyChange,
  onSaved,
  saveRef,
  discardRef
}) {
  const { setSnack } = React.useContext(SnackContext);

  const initialIds = React.useMemo(
    () =>
      Array.isArray(user?.externalFamilyIds) ? user.externalFamilyIds : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [ids, setIds] = React.useState(initialIds);
  // What Discard reverts to. Seeded from the loaded family, then RE-BASED on every successful save —
  // the memo above is frozen on mount, so without this "save ABC, then Discard All" put the field
  // back to the pre-save value while the server held ABC (TGCLOUD-1666).
  const savedIdsRef = React.useRef(initialIds);

  const handleChange = (_, newValue) => {
    setIds(newValue);
    onDirtyChange?.(true);
  };

  const promoteToPrimary = (option) => {
    setIds((prev) => {
      if (!prev.includes(option) || prev[0] === option) return prev;
      const next = [option, ...prev.filter((id) => id !== option)];
      return next;
    });
    onDirtyChange?.(true);
  };

  const onSave = async () => {
    await axios
      .put(APICONSTANTS.setExternalFamilyIds.replace(':user_id', user._id), {
        externalFamilyIds: ids
      })
      .then(({ data }) => {
        // The route trims each entry and drops blanks, so the server's array is the truth — for the
        // field, for the Discard baseline, and for whatever the parent keeps in sync.
        const saved = data.externalFamilyIds;
        setIds(saved);
        savedIdsRef.current = saved;
        onDirtyChange?.(false);
        onSaved?.(saved);
        setSnack({
          status: true,
          msg: 'External Family IDs updated',
          severity: 'success'
        });
      })
      .catch(() => {
        setSnack({ status: true, msg: 'An error occurred', severity: 'error' });
      });
  };

  const onDiscard = () => {
    setIds(savedIdsRef.current);
    onDirtyChange?.(false);
  };

  React.useEffect(() => {
    if (saveRef) saveRef.current = onSave;
    if (discardRef) discardRef.current = onDiscard;
  });

  return (
    <Grid container spacing={1}>
      <Grid size={12}>
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={ids}
          onChange={handleChange}
          renderValue={(value, getItemProps) =>
            value.map((option, index) => {
              const isPrimary = index === 0;
              const tagProps = getItemProps({ index });
              return (
                <Chip
                  {...tagProps}
                  key={option}
                  label={option}
                  size="small"
                  icon={
                    isPrimary ? (
                      <StarIcon
                        sx={{
                          fontSize: 14,
                          color: '#1a7a8a !important'
                        }}
                      />
                    ) : (
                      <StarBorderIcon
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          promoteToPrimary(option);
                        }}
                        sx={{
                          fontSize: 14,
                          cursor: 'pointer',
                          color: 'rgba(0,0,0,0.4) !important',
                          '&:hover': { color: '#1a7a8a !important' }
                        }}
                      />
                    )
                  }
                  sx={{
                    backgroundColor: isPrimary
                      ? 'rgba(43, 169, 191, 0.18)'
                      : 'rgba(43, 169, 191, 0.08)',
                    color: '#1a7a8a',
                    fontSize: '0.75rem',
                    fontWeight: isPrimary ? 700 : 500,
                    borderRadius: '10px',
                    border: isPrimary
                      ? '1px solid rgba(43, 169, 191, 0.5)'
                      : '1px solid transparent',
                    '& .MuiChip-deleteIcon': {
                      color: '#1a7a8a',
                      '&:hover': { color: '#0f5a67' }
                    }
                  }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="External Family IDs"
              placeholder="Type an ID and press Enter"
              size="small"
            />
          )}
        />
      </Grid>
      <Grid size={12}>
        <Typography
          sx={{
            fontSize: 11,
            color: 'text.secondary',
            lineHeight: 1.4
          }}
        >
          The starred ID is the primary alias. Click an outlined star to promote
          that ID — it becomes the headline when your display label mode is set
          to &ldquo;Primary external ID&rdquo;.
        </Typography>
      </Grid>
    </Grid>
  );
}
